import { FETCH_QUERIES_NAME, NULL_OPEN_PORTAL_EVENT } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import {
  Button,
  ComponentType,
  FileTypes,
  Form,
  FormFileUploader,
  Icon,
  LoadingButton,
  ModalBodyExtras,
  requiredRule,
  useForm,
  useModal,
} from '@gcl/react-core';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Note } from '@core/Note/Note';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { ICreateMySignatureRequest, mySignatureFormKeys } from './userProfile.models';
import { useCreateMySignatureAsync } from './userProfile.queries';

export interface IUseMySignatureModalProps {}

export const useMySignatureModal = ({}: IUseMySignatureModalProps) => {
  const { openPortal, closePortal, renderModal } = useModal();
  const queryClient = useQueryClient();

  const openMySignatureModal = () => {
    openPortal(NULL_OPEN_PORTAL_EVENT);
  };

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const { setError } = useFormMethods;

  const {
    mutate: mutateCreateMySignature,
    isLoading: isLoadingCreateMySignature,
    data: dataCreateMySignature,
    isSuccess: isSuccessCreateMySignature,
    isError: isErrorCreateMySignature,
    error: errorCreateMySignature,
    reset: resetCreateMySignature,
  } = useCreateMySignatureAsync();

  React.useEffect(() => {
    if (isSuccessCreateMySignature) {
      closePortal();
      queryClient.invalidateQueries([FETCH_QUERIES_NAME.GET_MY_SIGNATURE]);
    }
  }, [isSuccessCreateMySignature]);

  const onSubmit = (data: { [x: string]: any }) => {
    const request: ICreateMySignatureRequest = {
      fileContent: data.fileContent.files[0],
      fileName: data.fileContent.files[0].name,
    };
    mutateCreateMySignature(request);
  };

  const renderMySignatureModal = () => {
    return renderModal({
      large: true,
      title: `Upload Signature`,
      onRenderBody: (bodyExtras?: ModalBodyExtras) => {
        return (
          <div>
            <ApiErrorDisplay
              errors={[
                {
                  error: errorCreateMySignature,
                  genericError: 'Create signature failed',
                  reset: resetCreateMySignature,
                },
              ]}
            />
            <Form
              useFormMethods={useFormMethods}
              onSubmit={onSubmit}
              render={(formFieldMethods) => {
                return (
                  <>
                    <Note
                      prefixIcon={<Icon iconName="Lightbulb" />}
                      className="gcl-mb-md"
                      componentType={ComponentType.Warning}
                    >
                      Only required to upload once across projects.
                    </Note>
                    <LabelWithMessage required labelFor={mySignatureFormKeys.FILE_CONTENT} text="Signature" />
                    <FormFileUploader
                      acceptFileTypes={[FileTypes.Image]}
                      id={mySignatureFormKeys.FILE_CONTENT}
                      formFieldMethods={formFieldMethods}
                      rules={requiredRule}
                    />
                  </>
                );
              }}
            />
          </div>
        );
      },
      onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
        return (
          <>
            <LoadingButton
              type={ComponentType.Primary}
              onClick={() => {
                useFormMethods.handleSubmit(onSubmit)();
              }}
              isLoading={isLoadingCreateMySignature}
              isSuccess={isSuccessCreateMySignature}
              isError={isErrorCreateMySignature}
            >
              Save
            </LoadingButton>
            <Button
              prefixIconName="ChevronLeft"
              type={ComponentType.Secondary}
              onClick={() => {
                if (onHide) {
                  onHide();
                }
              }}
              style={{ margin: '10px' }}
            >
              Cancel
            </Button>
          </>
        );
      },
    });
  };
  return {
    openPortal: openMySignatureModal,
    closePortal,
    renderModal: renderMySignatureModal,
  };
};
